import { createContext, useContext, useState, useEffect, useRef, useCallback, ReactNode } from 'react';
import axios, { clearSessionTokenCache } from '../lib/axios';
import { useMultiStore, MultiStoreProvider } from './MultiStoreContext';
import { trackEvent, identifyUser, resetAnalytics, setMerchantSuperProps } from '../services/analytics';

/** App auth user (renamed from `User` to avoid merging with DOM `User`). */
export interface AuthUser {
    id: number;
    merchant_id?: number | null;
    name: string;
    email: string;
    provider?: string | null;
    provider_id?: string | null;
    email_verified_at: string | null;
    phone_number: string | null;
    phone_verified_at: string | null;
    password_reset_required?: boolean;
    /** 0 = show first-time connection on-boarding; 1 = completed (Enter Neo or Skip). */
    skip?: number;
    advanced_settings?: {
        card_chart_type?: string;
        card_chart_enabled?: boolean;
        isight_analysis_daily_report?: string;
        isight_ai_prediction?: string;
        netty_ai?: string;
        ai_intelligence?: string;
        whatsapp_sender_number?: string;
        whatsapp_config_api_key?: string;
        whatsapp_config_merchant_id?: string;
        whatsapp_config_cron_time?: string;
        default_currency?: string;
        sight_enabled?: boolean | number;
    } | null;
    is_deactivated?: boolean;
    deactivated_at?: string | null;
    deactivated_reason?: string | null;
    deactivated_until?: string | null;
    pending_delete?: boolean;
    uninstalled?: boolean;
}

export interface StoreSummary {
    id: number;
    encrypted_id?: string;
    user_id: number | null;
    shop: string;
    name: string;
    email: string | null;
    connected: boolean;
    connected_at?: string | null;
    theme_preference?: string | null;
    currency: string;
    shop_contact_number?: string | null;
    shop_address1?: string | null;
    shop_address2?: string | null;
    shop_city?: string | null;
    shop_province?: string | null;
    shop_province_code?: string | null;
    shop_zip?: string | null;
    shop_country?: string | null;
    shop_country_code?: string | null;
    shop_country_name?: string | null;
    shop_domain?: string | null;
    shop_owner?: string | null;
    shop_timezone?: string | null;
    shop_plan_display_name?: string | null;
    shop_customer_email?: string | null;
    user_role?: string;
    platform_type?: string;
}

export interface PlanFeatureItem {
    id: number | null;
    name: string;
    description: string | null;
    value_type: string;
    value_boolean: boolean;
    value_numeric: number | null;
    value_text: string | null;
    display_string: string | null;
    item?: {
        id: number;
        name: string;
        description: string | null;
    };
}

export interface PlanFeatureGroup {
    id: string | number;
    name: string;
    description: string | null;
    features: PlanFeatureItem[];
}

export interface Plan {
    name: string;
    price: number;
    order_limit: number;
    trial_days: number;
    features?: string[];
    prediction_limit?: number;
    allows_predictions?: boolean;
    unlimited_predictions?: boolean;
    // Dynamic plan features from DB
    hierarchical_features?: PlanFeatureGroup[];
    widget_limit?: number;
    connection_limit?: number;
    available_connections?: string[];
    // Notification limits
    email_notification_limit?: number;
    whatsapp_notification_limit?: number;
}

interface Subscription {
    plan_key: string;
    status: string;
    trial_ends_at: string | null;
    trial_days_remaining: number;
    charge_confirmation_url?: string | null;
    ends_at?: string | null;
    expires_at?: string | null;
    current_period_end?: string | null;
}

interface AuthContextType {
    user: AuthUser | null;
    token: string | null;
    stores: StoreSummary[];
    currentShop: string | null;
    loading: boolean;
    currentPlan: Plan | null;
    currentSubscription: Subscription | null;
    isFreePlan: boolean;
    billingDaysRemaining: number | null;
    hasFeature: (feature: string) => boolean;
    /** Check a DB-driven plan feature by item name (case-insensitive substring match). Returns boolean value. */
    hasPlanFeature: (featureName: string) => boolean;
    /** Get numeric value of a DB-driven plan feature (e.g. email_limit, whatsapp_limit). Returns null if not found. */
    getPlanFeatureNumeric: (featureName: string) => number | null;
    /** Get text value of a DB-driven plan feature. Returns null if not found. */
    getPlanFeatureText: (featureName: string) => string | null;
    canUsePredictions: () => boolean;
    getPredictionLimit: () => number;
    hasUnlimitedPredictions: () => boolean;
    login: (email: string, password: string) => Promise<boolean>;
    register: (
        name: string,
        email: string,
        password: string,
        websiteUrl?: string,
        passwordConfirmation?: string,
        phoneNumber?: string
    ) => Promise<{ success: boolean; error?: string }>;
    logout: (redirectUrl?: string) => Promise<void>;
    loginWithGoogle: () => Promise<{ success: boolean; error?: string }>;
    loginWithFacebook: () => Promise<{ success: boolean; error?: string }>;
    sendLoginOtp: (identifier: string, type: 'email' | 'phone') => Promise<{ success: boolean; error?: string; expiresIn?: number }>;
    verifyLoginOtp: (identifier: string, otp: string, type: 'email' | 'phone') => Promise<{ success: boolean; error?: string }>;
    resendLoginOtp: (identifier: string, type: 'email' | 'phone') => Promise<{ success: boolean; error?: string; expiresIn?: number }>;
    checkAuth: (force?: boolean) => Promise<void>;
    selectStore: (shop: string | null) => void;
    refreshStores: () => Promise<void>;
    loadSubscriptionData: (shop: string) => Promise<void>;
    updateUser: (userData: Partial<AuthUser>) => void;
    sessionExpiresAt?: number | null;
    isLoggingOut?: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);
const AUTH_LOGIN_PROVIDER_KEY = 'auth_login_provider';

export function useAuth() {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
}

interface AuthProviderProps {
    children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
    // MultiStoreContext is mounted by this provider (see EnhancedAuthProvider)
    // but it never fetched its own store list, so its activeStore/userStores
    // stayed empty. Role/permission consumers that read from it therefore saw
    // user_role === undefined and denied everything. Mirror the authoritative
    // AuthContext store payload into it below.
    const { setUserStores: setMultiStoreStores, setActiveStore: setMultiStoreActiveStore, clearActiveStore: clearMultiStoreActiveStore } = useMultiStore();

    const setAxiosAuthHeader = (jwt: string | null) => {
        if (jwt) {
            axios.defaults.headers.common['Authorization'] = `Bearer ${jwt}`;
        } else {
            delete axios.defaults.headers.common['Authorization'];
        }
    };

    const savedToken = typeof window !== 'undefined' ? localStorage.getItem('token') : null;
    const savedUserStr = typeof window !== 'undefined' ? localStorage.getItem('user') : null;
    let savedUser: AuthUser | null = null;
    if (savedUserStr) {
        try {
            savedUser = JSON.parse(savedUserStr);
        } catch (e) {
            // Invalid JSON, ignore
        }
    }

    const savedStoresStr = typeof window !== 'undefined' ? localStorage.getItem('stores') : null;
    let savedStores: StoreSummary[] = [];
    if (savedStoresStr) {
        try {
            savedStores = JSON.parse(savedStoresStr);
        } catch (e) {
            savedStores = [];
        }
    }

    const savedShop = typeof window !== 'undefined'
        ? localStorage.getItem('shop') || savedStores[0]?.shop || null
        : null;

    const [user, setUser] = useState<AuthUser | null>(savedUser);
    const [token, setToken] = useState<string | null>(savedToken);
    const [stores, setStoresState] = useState<StoreSummary[]>(savedStores);
    const [currentShop, setCurrentShop] = useState<string | null>(savedShop);
    const [loading, setLoading] = useState(true);
    const [currentPlan, setCurrentPlan] = useState<Plan | null>(null);
    const [currentSubscription, setCurrentSubscription] = useState<Subscription | null>(null);
    const [sessionExpiresAt, setSessionExpiresAt] = useState<number | null>(null);

    // Refs to prevent duplicate checkAuth calls
    const checkingAuthRef = useRef(false);
    const lastCheckAuthRef = useRef<number>(0);
    const STORE_CONNECTED_PREV_KEY = "netsight_prev_connected_shops";

    const persistStores = (storeList: StoreSummary[]) => {
        setStoresState(storeList);
        if (typeof window !== 'undefined') {
            if (storeList.length) {
                localStorage.setItem('stores', JSON.stringify(storeList));
            } else {
                localStorage.removeItem('stores');
            }
        }
    };

    // ─── FIX 1: wrap loadSubscriptionData in useCallback with no deps ────────
    // Previously this was a plain async function recreated on every render,
    // causing any useEffect that listed it as a dep to fire in an infinite loop.
    const loadSubscriptionData = useCallback(async (shop: string) => {
        try {
            const response = await axios.get("/billing/choose-plan", {
                params: { shop, account_settings: 'true' },
            });

            if (response.data?.current_subscription && response.data?.plans) {
                const subscription = response.data.current_subscription;
                const plans = response.data.plans;
                const plan = plans[subscription.plan_key];

                setCurrentSubscription(subscription);
                setCurrentPlan(plan || null);
            } else {
                setCurrentSubscription(null);
                setCurrentPlan(null);
            }
        } catch (error) {
            console.log('Failed to load subscription data:', (error as any)?.message);
            setCurrentSubscription(null);
            setCurrentPlan(null);
        }
    }, []); // no deps - only uses setters (stable) and axios (module-level stable)

    const selectStore = async (shop: string | null) => {
        if (shop) {
            setCurrentShop(shop);
            if (typeof window !== 'undefined') {
                localStorage.setItem('shop', shop);
            }

            const store = stores.find(s => s.shop === shop);

            // Mirror the chosen store (with its user_role) into MultiStoreContext
            // so role/permission consumers resolve immediately.
            if (store) {
                setMultiStoreActiveStore(store);
            }

            if (store) {
            trackEvent('Store Selected', {
                store_id: store.id,
                store_name: store.name,
                store_shop: store.shop,
                platform: store.platform_type,
            });
            setMerchantSuperProps({
                merchant_id: user?.merchant_id ?? store.id,
                store_id: store.id,
                store_name: store.name,
                shop: store.shop,
                platform: store.platform_type,
                currency: store.currency,
            });
            try {
                console.log('Saving last selected store to DB:', store.id, store.shop);
                const response = await axios.post(`/stores/${store.id}/switch`);
                console.log('Store saved successfully:', response.data);
            } catch (error) {
                    console.error('Error saving last selected store:', error);
                    if ((error as any).response) {
                        console.error('Response data:', (error as any).response.data);
                        console.error('Response status:', (error as any).response.status);
                    }
                }
            }

            if (typeof window !== 'undefined') {
                window.dispatchEvent(new CustomEvent('store_switched', {
                    detail: { store: shop }
                }));
            }

            // Note: Removed automatic redirect to /neo-intelligence.
            // Navigation is now handled individually by callers (e.g. post-login flow, store switcher).

            try {
                await loadSubscriptionData(shop);
            } catch (error) {
                console.log('Error loading subscription:', (error as any)?.message);
            }
        } else {
            setCurrentShop(null);
            setCurrentSubscription(null);
            setCurrentPlan(null);
            if (typeof window !== 'undefined') {
                localStorage.removeItem('shop');
            }
            clearMultiStoreActiveStore();
        }
    };

    const applyStores = (storeList: StoreSummary[]) => {
        persistStores(storeList);
        // Publish the authoritative store list (user_role + permissions) to the
        // MultiStoreContext mirror consumed by role/permission hooks.
        setMultiStoreStores(storeList);

        if (!storeList.length) {
            if (typeof window !== "undefined") {
                try { sessionStorage.removeItem(STORE_CONNECTED_PREV_KEY); } catch (_) { }
            }
            selectStore(null);
            return;
        }

        const currentConnectedShops = new Set(
            storeList.filter((s) => s.connected).map((s) => s.shop)
        );
        let prev = new Set<string>();
        if (typeof window !== "undefined") {
            try {
                const raw = sessionStorage.getItem(STORE_CONNECTED_PREV_KEY);
                if (raw) {
                    const arr = JSON.parse(raw);
                    if (Array.isArray(arr)) prev = new Set(arr);
                }
            } catch (_) { }
        }
        if (prev.size > 0) {
            currentConnectedShops.forEach((shop) => {
                if (!prev.has(shop) && typeof window !== "undefined") {
                    window.dispatchEvent(
                        new CustomEvent("storeConnected", { detail: { shop } })
                    );
                }
            });
        }
        if (typeof window !== "undefined") {
            try {
                sessionStorage.setItem(
                    STORE_CONNECTED_PREV_KEY,
                    JSON.stringify([...currentConnectedShops])
                );
            } catch (_) { }
        }

        const activeStoreId = localStorage.getItem('activeStoreId');
        if (activeStoreId) {
            const explicitStore = storeList.find(s => s.id === parseInt(activeStoreId, 10));
            if (explicitStore) {
                if (explicitStore.shop !== currentShop) {
                    selectStore(explicitStore.shop);
                }
                return;
            }
        }

        const connectedStore = storeList.find(store =>
            store.connected && store.shop !== 'temp-store'
        );
        if (connectedStore) {
            const currentStore = storeList.find(store =>
                store.shop === currentShop && store.shop !== 'temp-store'
            );
            if (currentStore && currentStore.connected) {
                return;
            }
            selectStore(connectedStore.shop);
            return;
        }

        const currentStore = storeList.find(store => store.shop === currentShop);
        if (currentStore) {
            return;
        }

        const nextStore = storeList[0] ?? null;
        if (nextStore) {
            selectStore(nextStore.shop);
        } else {
            selectStore(null);
        }
    };

    // ─── FIX 2: wrap checkAuth in useCallback with no deps ──────────────────
    // Previously a plain function, it was listed in the cross-tab useEffect dep
    // array and recreated every render, causing that effect to re-subscribe and
    // potentially re-run checkAuth in a loop.
    const checkAuth = useCallback(async (force = false) => {
        if (typeof window !== 'undefined' && localStorage.getItem('is_logging_out') === 'true') {
            // console.log('Skipping checkAuth during logout');
            return;
        }

        const now = Date.now();
        if (!force && (checkingAuthRef.current || (now - lastCheckAuthRef.current) < 2000)) {
            return;
        }

        checkingAuthRef.current = true;
        lastCheckAuthRef.current = now;

        const savedToken = typeof window !== 'undefined' ? localStorage.getItem('token') : null;
        if (!savedToken) {
            setUser(null);
            setToken(null);
            setAxiosAuthHeader(null);
            applyStores([]);
            setLoading(false);
            // No authenticated token: make sure no session token from a prior
            // account is retained in memory.
            clearSessionTokenCache();
            checkingAuthRef.current = false;
            return;
        }

        setToken(savedToken);
        setAxiosAuthHeader(savedToken);

        const savedUserStr = localStorage.getItem('user');
        if (savedUserStr) {
            try {
                const parsedUser = JSON.parse(savedUserStr);
                setUser(parsedUser);
            } catch (e) {
                // Invalid JSON, ignore
            }
        }

        const savedStoresStr = localStorage.getItem('stores');
        if (savedStoresStr) {
            try {
                const parsedStores = JSON.parse(savedStoresStr);
                applyStores(parsedStores);
            } catch (e) {
                // Invalid JSON, ignore
            }
        }

        try {
            const response = await axios.get('/me');

            if (response.status === 200 && response.data.user) {
                if (response.data.user.is_deactivated) {
                    if (typeof window !== 'undefined') {
                        if (window.location.pathname !== '/account-reactivate') {
                            window.location.href = '/account-reactivate';
                        }
                    }
                    return;
                }

                setUser(response.data.user);
                setToken(savedToken);
                setAxiosAuthHeader(savedToken);
                if (typeof window !== 'undefined') {
                    localStorage.setItem('user', JSON.stringify(response.data.user));
                }
                if (response.data.stores) {
                    applyStores(response.data.stores);
                }
            } else {
                console.log('No authenticated user in response, keeping existing state');
            }
        } catch (error: any) {
            if (error.response?.status === 401 || error.response?.status === 403) {
                const message = String(error.response?.data?.message || '').toLowerCase();
                const shouldClearAuth =
                    message.includes('token expired') ||
                    message.includes('token invalid') ||
                    message.includes('invalid token') ||
                    message.includes('signature') ||
                    message.includes('unauthenticated');

                if (shouldClearAuth) {
                    console.log('Authentication check failed: token is invalid/expired');
                    setUser(null);
                    setToken(null);
                    setAxiosAuthHeader(null);
                    applyStores([]);
                    setLoading(false);
                    if (typeof window !== 'undefined') {
                        localStorage.removeItem('token');
                        localStorage.removeItem('user');
                    }
                } else {
                    console.log('Authentication check returned 401/403 without invalid-token signal; keeping session');
                }
            } else {
                console.log('Authentication check failed (network error):', error.message);
            }
        } finally {
            setLoading(false);
            checkingAuthRef.current = false;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); // no deps - uses only refs (stable) and setters (stable)

    const refreshStores = async () => {
        try {
            const response = await axios.get('/me');
            if (response.status === 200) {
                if (response.data.user) {
                    setUser(response.data.user);
                    if (typeof window !== 'undefined') {
                        localStorage.setItem('user', JSON.stringify(response.data.user));
                    }
                }
                if (response.data.stores) {
                    applyStores(response.data.stores);
                }
            }
        } catch (error) {
            console.log('Failed to refresh stores:', (error as any)?.message);
        }
    };

    // Keep the MultiStoreContext mirror's activeStore aligned with the
    // authoritative currentShop on every render path (restore from /me,
    // hydrated localStorage state, store switch, etc.).
    useEffect(() => {
        if (!currentShop) return;
        const match = stores.find((s) => s.shop === currentShop);
        if (match) {
            setMultiStoreActiveStore(match);
        }
    }, [currentShop, stores, setMultiStoreActiveStore]);

    const updateUser = (userData: Partial<AuthUser>) => {
        if (user) {
            const updatedUser = { ...user, ...userData };
            setUser(updatedUser);
            if (typeof window !== 'undefined') {
                localStorage.setItem('user', JSON.stringify(updatedUser));
            }
        }
    };

    const login = async (email: string, password: string): Promise<boolean> => {
        try {
            // console.log('Attempting login...');
            const response = await axios.post('/login', { login: email, password });

            // console.log('Login response:', response.status, response.statusText);

            if (response.status === 200 && response.data.token) {
                // A new identity is signing in: drop any session token cached for
                // the previous account before issuing further requests.
                clearSessionTokenCache();
                // console.log('Login successful, user data:', response.data);
                setUser(response.data.user);
                setToken(response.data.token);
                setAxiosAuthHeader(response.data.token);
                if (typeof window !== 'undefined') {
                    localStorage.setItem('token', response.data.token);
                    localStorage.setItem('user', JSON.stringify(response.data.user));

                    const postLogoutRedirect = localStorage.getItem('post_logout_redirect');
                    if (postLogoutRedirect) {
                        localStorage.removeItem('post_logout_redirect');
                        localStorage.removeItem('is_logging_out');
                        setTimeout(() => {
                            window.location.href = postLogoutRedirect;
                        }, 100);
                    }

                    localStorage.setItem(
                        'auth_event',
                        JSON.stringify({ type: 'login', at: Date.now() })
                    );
                    window.dispatchEvent(new CustomEvent('auth_login'));
                    trackEvent('Login', {
                        user_id: response.data.user?.id,
                        email: response.data.user?.email,
                        provider: response.data.user?.provider,
                    });
                    identifyUser(response.data.user?.id, {
                        $email: response.data.user?.email,
                        $name: response.data.user?.name,
                        provider: response.data.user?.provider,
                    });
                    setMerchantSuperProps({ merchant_id: response.data.user?.merchant_id ?? response.data.user?.id });
                }
                // if (response.data.stores) {
                //     applyStores(response.data.stores);
                // } else {
                //     applyStores([]);
                //     setLoading(false);
                // }
                if (response.data.stores) {
                    applyStores(response.data.stores);

                    const connectedStore = response.data.stores.find(
                        (store: StoreSummary) =>
                            store.connected && store.shop !== 'temp-store'
                    );

                    // Invited team members (non-owner shared access) can never connect a
                    // store, so they must not be pushed to the connection on-boarding screen.
                    const sharedRoles = ['member', 'admin', 'manager', 'analyst'];
                    const hasSharedAccess = response.data.stores.some(
                        (store: StoreSummary) =>
                            sharedRoles.includes(String(store.user_role)) &&
                            store.shop !== 'temp-store'
                    );

                    const postLogoutRedirect = localStorage.getItem('post_logout_redirect');

                    // No connected store → on-boarding (owners only)
                    if (!connectedStore && !hasSharedAccess && !postLogoutRedirect && typeof window !== 'undefined') {
                        setTimeout(() => {
                            window.location.href = '/on-boarding';
                        }, 100);

                        return true;
                    }

                } else {
                    applyStores([]);
                    setLoading(false);

                    // An invited team member who has NOT yet accepted has no stores.
                    // Route to "/" so UnifiedGuard can show the invitation-accept modal
                    // instead of the connect on-boarding screen they can't use.
                    const hasPendingInvite =
                        !!response.data.invitation_accepted ||
                        !!response.data.invitation_available ||
                        !!response.data.invitation_token ||
                        (typeof window !== 'undefined' &&
                            (!!localStorage.getItem('invitation_token') ||
                                !!localStorage.getItem('invitation_data') ||
                                !!localStorage.getItem('pending_invitation_data')));

                    if (typeof window !== 'undefined') {
                        setTimeout(() => {
                            window.location.href = hasPendingInvite ? '/' : '/on-boarding';
                        }, 100);
                    }

                    return true;
                }
                if (response.data.invitation_accepted) {
                    // console.log('Invitation available for acceptance:', response.data.invitation_message);
                    if (typeof window !== 'undefined') {
                        localStorage.setItem('pending_invitation_data', JSON.stringify({
                            message: response.data.invitation_message,
                            store: response.data.joined_store,
                            token: response.data.invitation_token
                        }));
                    }
                }

                return true;
            }
            // console.log('Login failed:', response.status);
            return false;
        } catch (error: any) {
            // console.log('Login failed:', error.response?.status || 'Network error');
            return false;
        }
    };

    const getRegistrationTrackingParams = () => {
        const trackingParams: Record<string, string> = {};

        if (typeof window === 'undefined') {
            return trackingParams;
        }

        const params = new URLSearchParams(window.location.search);
        const utmFields = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'] as const;

        utmFields.forEach((field) => {
            const value = params.get(field)?.trim();
            if (value) {
                trackingParams[field] = value;
            }
        });

        return trackingParams;
    };

    const register = async (
        name: string,
        email: string,
        password: string,
        websiteUrl?: string,
        passwordConfirmation?: string,
        phoneNumber?: string
    ): Promise<{ success: boolean; error?: string }> => {
        try {
            // console.log('Attempting registration...');
            const payload: any = { name, email, password, phone_number: phoneNumber };
            if (websiteUrl) payload.website_url = websiteUrl;
            if (passwordConfirmation) {
                payload.password_confirmation = passwordConfirmation;
            } else {
                return { success: false, error: "Password confirmation is required." };
            }

            if (typeof window !== 'undefined') {
                const invitationToken = sessionStorage.getItem('pending_invitation_token');
                if (invitationToken) {
                    payload.invitation_token = invitationToken;
                    // console.log('Including invitation token in registration:', invitationToken);
                }

                Object.assign(payload, getRegistrationTrackingParams());
            }
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!emailRegex.test(email)) {
                return { success: false, error: "Please enter a valid email address." };
            }
            const response = await axios.post('/register', payload);

            if (response.status === 200 && response.data.token) {
                // console.log('Registration successful, user data:', response.data);
                setUser(response.data.user);
                setToken(response.data.token);
                setAxiosAuthHeader(response.data.token);
                if (typeof window !== 'undefined') {
                    localStorage.setItem('token', response.data.token);
                    localStorage.setItem('user', JSON.stringify(response.data.user));
                }
                trackEvent('Sign Up', {
                    user_id: response.data.user?.id,
                    email: response.data.user?.email,
                    name: response.data.user?.name,
                });
                identifyUser(response.data.user?.id, {
                    $email: response.data.user?.email,
                    $name: response.data.user?.name,
                    signup_date: new Date().toISOString(),
                });
                setMerchantSuperProps({ merchant_id: response.data.user?.merchant_id ?? response.data.user?.id });
                if (response.data.stores) {
                    applyStores(response.data.stores);
                } else {
                    applyStores([]);
                    setLoading(false);
                }

                if (response.data.invitation_accepted) {
                    // console.log('Invitation available for acceptance:', response.data.invitation_message);
                    if (typeof window !== 'undefined') {
                        localStorage.setItem('pending_invitation_data', JSON.stringify({
                            message: response.data.invitation_message,
                            store: response.data.joined_store,
                            token: response.data.invitation_token
                        }));
                    }
                }

                return { success: true };
            }
            return { success: false, error: 'Registration failed' };
        } catch (error: any) {
            // console.log('Registration failed:', error.response?.data || error.message);
            const errorMessage = error.response?.data?.message
                || error.response?.data?.errors?.email?.[0]
                || error.response?.data?.error
                || 'Registration failed. Please try again.';
            return { success: false, error: errorMessage };
        }
    };

    const logout = async (redirectUrl?: string) => {
        try {
            const currentToken = token;
            if (currentToken) {
                await axios.post('/logout', {}, {
                    headers: { 'Authorization': `Bearer ${currentToken}` }
                });
            }
            // console.log('Logout successful');
        } catch (error: any) {
            // console.log('Logout failed:', error.response?.status || 'Network error');
        } finally {
            trackEvent('Logout', { user_id: user?.id });
            resetAnalytics();
            setUser(null);
            setToken(null);
            setAxiosAuthHeader(null);
            applyStores([]);
            setLoading(false);

            // Drop any cached Shopify session token immediately so it can never
            // be replayed for the next account signing in on this browser.
            clearSessionTokenCache();

            if (typeof window !== 'undefined') {
                localStorage.setItem('is_logging_out', 'true');
                localStorage.removeItem('token');
                localStorage.removeItem('user');
                localStorage.removeItem('shop');
                localStorage.removeItem('stores');
                // Clear the persisted active-store identity. Without this, the
                // previous account's shop/store survives logout and is picked up
                // by ActiveStoreContext/SightContext after the next login.
                localStorage.removeItem('activeStore');
                localStorage.removeItem('activeStoreId');
                localStorage.removeItem('activeStoreShop');
                sessionStorage.removeItem('trial_modal_shown');

                setTimeout(() => {
                    localStorage.removeItem('is_logging_out');
                }, 200);

                if (redirectUrl) {
                    localStorage.setItem('post_logout_redirect', redirectUrl);
                }

                localStorage.removeItem(AUTH_LOGIN_PROVIDER_KEY);
                localStorage.setItem(
                    'auth_event',
                    JSON.stringify({ type: 'logout', at: Date.now() })
                );
                // console.log("inform i am log out");

                setTimeout(() => {
                    window.location.href = '/signin';
                }, 100);
            }
        }
    };

    const sendLoginOtp = async (identifier: string, type: 'email' | 'phone'): Promise<{ success: boolean; error?: string; expiresIn?: number }> => {
        try {
            const response = await axios.post('/otp/send', {
                identifier,
                type,
            });

            if (response.data.success) {
                return {
                    success: true,
                    expiresIn: response.data.expires_in,
                };
            } else {
                return {
                    success: false,
                    error: response.data.error || 'Failed to send OTP',
                };
            }
        } catch (error: any) {
            console.error('Error sending login OTP:', error);
            const errorMessage = error.response?.data?.error || 'Failed to send OTP. Please try again.';
            return {
                success: false,
                error: errorMessage,
            };
        }
    };

    const verifyLoginOtp = async (identifier: string, otp: string, type: 'email' | 'phone'): Promise<{ success: boolean; error?: string }> => {
        try {
            const response = await axios.post('/otp/verify', {
                identifier,
                otp,
                type,
            });

            if (response.data.token) {
                // console.log('OTP login successful, user data:', response.data);
                setUser(response.data.user);
                setToken(response.data.token);
                setAxiosAuthHeader(response.data.token);
                if (typeof window !== 'undefined') {
                    localStorage.setItem('token', response.data.token);
                    localStorage.setItem('user', JSON.stringify(response.data.user));

                    const postLogoutRedirect = localStorage.getItem('post_logout_redirect');
                    if (postLogoutRedirect) {
                        localStorage.removeItem('post_logout_redirect');
                        localStorage.removeItem('is_logging_out');
                        setTimeout(() => {
                            window.location.href = postLogoutRedirect;
                        }, 100);
                    }

                    localStorage.setItem(
                        'auth_event',
                        JSON.stringify({ type: 'login', at: Date.now() })
                    );
                    window.dispatchEvent(new CustomEvent('auth_login'));
                    trackEvent('Login', {
                        user_id: response.data.user?.id,
                        email: response.data.user?.email,
                        provider: 'otp',
                    });
                    identifyUser(response.data.user?.id, {
                        $email: response.data.user?.email,
                        $name: response.data.user?.name,
                        provider: 'otp',
                    });
                    setMerchantSuperProps({ merchant_id: response.data.user?.merchant_id ?? response.data.user?.id });
                }

                if (response.data.stores) {
                    applyStores(response.data.stores);

                    const connectedStore = response.data.stores.find(
                        (store: StoreSummary) =>
                            store.connected && store.shop !== 'temp-store'
                    );

                    // Invited team members (non-owner shared access) can never connect a
                    // store, so they must not be pushed to the connection on-boarding screen.
                    const sharedRoles = ['member', 'admin', 'manager', 'analyst'];
                    const hasSharedAccess = response.data.stores.some(
                        (store: StoreSummary) =>
                            sharedRoles.includes(String(store.user_role)) &&
                            store.shop !== 'temp-store'
                    );

                    const postLogoutRedirect = localStorage.getItem('post_logout_redirect');

                    if (!connectedStore && !hasSharedAccess && !postLogoutRedirect && typeof window !== 'undefined') {
                        setTimeout(() => {
                            window.location.href = '/on-boarding';
                        }, 100);

                        return { success: true };
                    }
                } else {
                    applyStores([]);
                    setLoading(false);

                    // An invited team member who has NOT yet accepted has no stores.
                    // Sending them to /on-boarding is wrong - they can't connect a store.
                    // Route to "/" so UnifiedGuard shows the invitation-accept modal instead.
                    const hasPendingInvite =
                        !!response.data.invitation_accepted ||
                        !!response.data.invitation_available ||
                        !!response.data.invitation_token ||
                        (typeof window !== 'undefined' &&
                            (!!localStorage.getItem('invitation_token') ||
                                !!localStorage.getItem('invitation_data') ||
                                !!localStorage.getItem('pending_invitation_data')));

                    if (typeof window !== 'undefined') {
                        setTimeout(() => {
                            window.location.href = hasPendingInvite ? '/' : '/on-boarding';
                        }, 100);
                    }

                    return { success: true };
                }

                return { success: true };
            } else {
                return {
                    success: false,
                    error: response.data.error || 'Invalid OTP',
                };
            }
        } catch (error: any) {
            console.error('Error verifying login OTP:', error);
            const errorMessage = error.response?.data?.error || 'Failed to verify OTP. Please try again.';
            return {
                success: false,
                error: errorMessage,
            };
        }
    };

    const resendLoginOtp = async (identifier: string, type: 'email' | 'phone'): Promise<{ success: boolean; error?: string; expiresIn?: number }> => {
        try {
            const response = await axios.post('/otp/resend', {
                identifier,
                type,
            });

            if (response.data.success) {
                return {
                    success: true,
                    expiresIn: response.data.expires_in,
                };
            } else {
                return {
                    success: false,
                    error: response.data.error || 'Failed to resend OTP',
                };
            }
        } catch (error: any) {
            console.error('Error resending login OTP:', error);
            const errorMessage = error.response?.data?.error || 'Failed to resend OTP. Please try again.';
            return {
                success: false,
                error: errorMessage,
            };
        }
    };

    // Bootstrap on mount
    useEffect(() => {
        const bootstrap = async () => {
            if (typeof window !== 'undefined') {
                const params = new URLSearchParams(window.location.search);
                const authToken = params.get('auth_token');
                const shopParam = params.get('shop');

                if (authToken) {
                    localStorage.setItem('token', authToken);
                    setToken(authToken);
                    setAxiosAuthHeader(authToken);
                    params.delete('auth_token');
                    const newSearch = params.toString();
                    window.history.replaceState(
                        {},
                        '',
                        `${window.location.pathname}${newSearch ? `?${newSearch}` : ''}${window.location.hash}`
                    );
                }

                if (shopParam) {
                    localStorage.setItem('shop', shopParam);
                    setCurrentShop(shopParam);
                    params.delete('shop');
                    const newSearch = params.toString();
                    window.history.replaceState(
                        {},
                        '',
                        `${window.location.pathname}${newSearch ? `?${newSearch}` : ''}${window.location.hash}`
                    );
                }
            }

            await checkAuth();
        };

        bootstrap();

        if (typeof window !== 'undefined') {
            const handleFocus = () => { checkAuth(); };
            window.addEventListener('focus', handleFocus);
            return () => window.removeEventListener('focus', handleFocus);
        }

        return;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); // mount-only - checkAuth is now stable via useCallback

    // Cross-tab auth sync
    // ─── FIX 3: checkAuth is now stable (useCallback), so this effect won't
    // re-subscribe on every render and won't trigger the infinite loop.
    useEffect(() => {
        if (typeof window === 'undefined') return;

        const handleStorage = (event: StorageEvent) => {
            if (event.key === 'auth_event' && event.newValue) {
                try {
                    const payload = JSON.parse(event.newValue);
                    if (!payload?.type) return;

                    if (payload.type === 'logout') {
                        setUser(null);
                        setToken(null);
                        setAxiosAuthHeader(null);
                        applyStores([]);
                        setLoading(false);
                        localStorage.removeItem('token');
                        localStorage.removeItem('user');
                        localStorage.removeItem('shop');
                        localStorage.removeItem('stores');
                    }

                    if (payload.type === 'login') {
                        checkAuth();
                    }
                } catch {
                    // Ignore malformed events
                }
            }
        };

        window.addEventListener('storage', handleStorage);
        return () => window.removeEventListener('storage', handleStorage);
    }, [checkAuth]); // safe now - checkAuth reference is stable

    const hasFeature = (feature: string): boolean => {
        if (!currentPlan?.features) return false;
        return currentPlan.features.some(f =>
            f.toLowerCase().includes(feature.toLowerCase())
        );
    };

    /**
     * Check a DB-driven plan feature by item name (case-insensitive substring match).
     * Looks through hierarchical_features → features[].item.name or features[].name.
     * Returns the boolean value of the feature, or false if not found.
     */
    const hasPlanFeature = (featureName: string): boolean => {
        if (!currentPlan?.hierarchical_features) return false;
        const needle = featureName.toLowerCase();
        for (const group of currentPlan.hierarchical_features) {
            for (const feat of group.features) {
                const itemName = (feat.item?.name ?? feat.name ?? '').toLowerCase();
                if (itemName.includes(needle)) {
                    // If value_type is boolean, return value_boolean
                    if (feat.value_type === 'boolean') return feat.value_boolean === true;
                    // If value_type is numeric, treat > 0 as true
                    if (feat.value_type === 'numeric') return (feat.value_numeric ?? 0) > 0;
                    // If value_type is text, treat non-empty as true
                    if (feat.value_type === 'text') return !!(feat.value_text);
                    // Fallback: if display_string exists, treat as true
                    return !!(feat.display_string);
                }
            }
        }
        return false;
    };

    /**
     * Get numeric value of a DB-driven plan feature.
     * Returns null if not found or not numeric.
     */
    const getPlanFeatureNumeric = (featureName: string): number | null => {
        if (!currentPlan?.hierarchical_features) return null;
        const needle = featureName.toLowerCase();
        for (const group of currentPlan.hierarchical_features) {
            for (const feat of group.features) {
                const itemName = (feat.item?.name ?? feat.name ?? '').toLowerCase();
                if (itemName.includes(needle)) {
                    return feat.value_numeric ?? null;
                }
            }
        }
        return null;
    };

    /**
     * Get text value of a DB-driven plan feature.
     * Returns null if not found.
     */
    const getPlanFeatureText = (featureName: string): string | null => {
        if (!currentPlan?.hierarchical_features) return null;
        const needle = featureName.toLowerCase();
        for (const group of currentPlan.hierarchical_features) {
            for (const feat of group.features) {
                const itemName = (feat.item?.name ?? feat.name ?? '').toLowerCase();
                if (itemName.includes(needle)) {
                    return feat.value_text ?? feat.display_string ?? null;
                }
            }
        }
        return null;
    };


    const subscriptionStatus = String(currentSubscription?.status ?? "").toLowerCase();

    const parseDateMs = (value?: string | null): number | null => {
        if (!value) return null;
        const ms = Date.parse(value);
        return Number.isFinite(ms) ? ms : null;
    };

    const billingEndsAtMs =
        parseDateMs(currentSubscription?.ends_at) ??
        parseDateMs(currentSubscription?.expires_at) ??
        parseDateMs(currentSubscription?.current_period_end) ??
        parseDateMs(currentSubscription?.trial_ends_at) ??
        null;

    const billingDaysRemaining = billingEndsAtMs
        ? Math.ceil((billingEndsAtMs - Date.now()) / (1000 * 60 * 60 * 24))
        : null;

    const isSubscriptionExpired =
        subscriptionStatus.includes("expired") ||
        subscriptionStatus.includes("cancel") ||
        subscriptionStatus.includes("inactive") ||
        subscriptionStatus.includes("past_due") ||
        subscriptionStatus.includes("unpaid");

    const isFreePlan =
        isSubscriptionExpired ||
        (currentSubscription?.plan_key ? String(currentSubscription.plan_key).toLowerCase().includes("free") : false) ||
        (currentPlan?.price ?? 0) <= 0 ||
        (currentPlan?.name ? String(currentPlan.name).toLowerCase().includes("free") : false);

    const canUsePredictions = (): boolean => currentPlan?.allows_predictions === true;
    const getPredictionLimit = (): number => currentPlan?.prediction_limit ?? 0;
    const hasUnlimitedPredictions = (): boolean => currentPlan?.unlimited_predictions === true;

    const loginWithGoogle = async (): Promise<{ success: boolean; error?: string }> => {
        try {
            // console.log('Attempting Google login...');

            const clientId = import.meta.env.VITE_GOOGLE_CLIENT_ID;
            if (!clientId) {
                return { success: false, error: 'Google OAuth not configured' };
            }

            const tokenBefore =
                typeof window !== 'undefined' ? localStorage.getItem('token') : null;

            const authUrl = `${import.meta.env.VITE_API_BASE_URL}/auth/google/redirect`;
            let popup: Window | null = null;

            return new Promise((resolve) => {
                let settled = false;
                let receivedMessage = false;
                let checkClosed: ReturnType<typeof setInterval> | null = null;

                const graceMs = 15000;
                const maxMs = 120000;
                const openedAt = Date.now();

                const cleanup = (handler: (event: MessageEvent) => void) => {
                    if (checkClosed) clearInterval(checkClosed);
                    window.removeEventListener('message', handler as EventListener);
                };

                const messageHandler = async (event: MessageEvent) => {
                    const data = event.data as any;
                    if (!data) return;

                    const tokenFromMessage = typeof data.token === 'string' ? data.token : null;
                    const type = typeof data.type === 'string' ? data.type : null;

                    if (!receivedMessage) {
                        receivedMessage = true;
                        console.debug('Google OAuth: postMessage received', {
                            origin: event.origin,
                            type,
                            hasToken: Boolean(tokenFromMessage),
                            hasError: Boolean(data.error || data.message),
                            keys: Object.keys(data || {}),
                        });
                    }

                    if (type === 'SOCIAL_AUTH_SUCCESS' || tokenFromMessage) {
                        settled = true;
                        if (checkClosed) clearInterval(checkClosed);
                        window.removeEventListener('message', messageHandler as EventListener);

                        const socialToken = (tokenFromMessage || data.token) as string;

                        try {
                            const response = await axios.post(`/auth/google`, {
                                token: socialToken,
                                ...getRegistrationTrackingParams(),
                            });
                            if (response.status === 200 && response.data.token) {
                                setUser(response.data.user);
                                setToken(response.data.token);
                                setAxiosAuthHeader(response.data.token);
                                if (typeof window !== 'undefined') {
                                    localStorage.setItem('token', response.data.token);
                                    localStorage.setItem('user', JSON.stringify(response.data.user));
                                    localStorage.setItem(AUTH_LOGIN_PROVIDER_KEY, 'google');
                                }
                                applyStores(response.data.stores ?? []);
                                trackEvent('Login', {
                                    user_id: response.data.user?.id,
                                    email: response.data.user?.email,
                                    provider: 'google',
                                });
                                identifyUser(response.data.user?.id, {
                                    $email: response.data.user?.email,
                                    $name: response.data.user?.name,
                                    provider: 'google',
                                });
                                setMerchantSuperProps({ merchant_id: response.data.user?.merchant_id ?? response.data.user?.id });
                                // The /auth/google store payload omits `user_role`, which
                                // UnifiedGuard needs to recognize an owner's connected store.
                                // Without it the guard treats the user as un-onboarded and
                                // pushes them to the connection page even after onboarding is
                                // complete. Re-hydrate from /me (which returns user_role) so
                                // routing matches the password/OTP flow.
                                await checkAuth(true);
                                resolve({ success: true });
                            } else {
                                const errorMsg = response.data.error || 'Authentication failed';
                                resolve({ success: false, error: errorMsg.includes('account with this email already exists') ? 'Account already exists. Please sign in with your password.' : errorMsg });
                            }
                        } catch (error: any) {
                            console.error('Social auth error:', error);
                            const errorMsg = error.response?.data?.error || error.message || 'Authentication failed';
                            resolve({
                                success: false,
                                error: errorMsg.includes('account with this email already exists')
                                    ? 'Account already exists. Please sign in with your password.'
                                    : errorMsg,
                            });
                        } finally {
                            cleanup(messageHandler);
                        }
                        return;
                    }

                    if (type === 'SOCIAL_AUTH_ERROR' || typeof data.error === 'string' || typeof data.message === 'string') {
                        settled = true;
                        if (checkClosed) clearInterval(checkClosed);
                        window.removeEventListener('message', messageHandler as EventListener);
                        resolve({
                            success: false,
                            error: data.message || data.error || 'Authentication failed',
                        });
                        cleanup(messageHandler);
                    }
                };

                window.addEventListener('message', messageHandler as EventListener);

                popup = window.open(authUrl, 'google-auth', 'width=500,height=600,scrollbars=yes');
                if (!popup) {
                    cleanup(messageHandler);
                    resolve({ success: false, error: 'Popup blocked' });
                    return;
                }

                checkClosed = setInterval(() => {
                    const now = Date.now();
                    if (settled) return;

                    if (now - openedAt > maxMs) {
                        if (popup && !popup.closed) return;
                        settled = true;
                        cleanup(messageHandler);
                        console.warn('Google OAuth: authentication cancelled (timeout)');
                        resolve({ success: false, error: 'Authentication cancelled (timeout)' });
                        return;
                    }

                    if (!receivedMessage && now - openedAt > graceMs) {
                        const tokenAfter = localStorage.getItem('token');
                        if (tokenAfter && tokenAfter !== tokenBefore) {
                            settled = true;
                            cleanup(messageHandler);
                            console.warn('Google OAuth: token appeared; treating as success');
                            void checkAuth().finally(() => resolve({ success: true }));
                            return;
                        }

                        if (popup && !popup.closed) return;
                        settled = true;
                        cleanup(messageHandler);
                        console.warn('Google OAuth: authentication cancelled (no postMessage after grace)');
                        resolve({ success: false, error: 'Authentication cancelled' });
                    }
                }, 500);
            });
        } catch (error) {
            console.error('Google login error:', error);
            return { success: false, error: 'Google login failed' };
        }
    };

    const loginWithFacebook = async (): Promise<{ success: boolean; error?: string }> => {
        try {
            // console.log('Attempting Facebook login...');

            const tokenBefore =
                typeof window !== 'undefined' ? localStorage.getItem('token') : null;

            const authUrl = `${import.meta.env.VITE_API_BASE_URL}/auth/facebook/redirect`;
            let popup: Window | null = null;

            return new Promise((resolve) => {
                let settled = false;
                let receivedMessage = false;
                let checkClosed: ReturnType<typeof setInterval> | null = null;

                const graceMs = 15000;
                const maxMs = 120000;
                const openedAt = Date.now();

                const cleanup = (handler: (event: MessageEvent) => void) => {
                    if (checkClosed) clearInterval(checkClosed);
                    window.removeEventListener('message', handler as EventListener);
                };

                const messageHandler = async (event: MessageEvent) => {
                    const data = event.data as any;
                    if (!data) return;

                    const tokenFromMessage = typeof data.token === 'string' ? data.token : null;
                    const type = typeof data.type === 'string' ? data.type : null;

                    if (!receivedMessage) {
                        receivedMessage = true;
                        console.debug('Facebook OAuth: postMessage received', {
                            origin: event.origin,
                            type,
                            hasToken: Boolean(tokenFromMessage),
                            hasError: Boolean(data.error || data.message),
                            keys: Object.keys(data || {}),
                        });
                    }

                    if (type === 'SOCIAL_AUTH_SUCCESS' || tokenFromMessage) {
                        settled = true;
                        if (checkClosed) clearInterval(checkClosed);
                        window.removeEventListener('message', messageHandler as EventListener);

                        const socialToken = (tokenFromMessage || data.token) as string;

                        try {
                            const response = await axios.post(`/auth/facebook`, { token: socialToken });
                            if (response.status === 200 && response.data.token) {
                                setUser(response.data.user);
                                setToken(response.data.token);
                                setAxiosAuthHeader(response.data.token);
                                if (typeof window !== 'undefined') {
                                    localStorage.setItem('token', response.data.token);
                                    localStorage.setItem('user', JSON.stringify(response.data.user));
                                    localStorage.setItem(AUTH_LOGIN_PROVIDER_KEY, 'facebook');
                                }
                                applyStores(response.data.stores ?? []);
                                trackEvent('Login', {
                                    user_id: response.data.user?.id,
                                    email: response.data.user?.email,
                                    provider: 'facebook',
                                });
                                identifyUser(response.data.user?.id, {
                                    $email: response.data.user?.email,
                                    $name: response.data.user?.name,
                                    provider: 'facebook',
                                });
                                setMerchantSuperProps({ merchant_id: response.data.user?.merchant_id ?? response.data.user?.id });
                                // The /auth/facebook store payload omits `user_role`, which
                                // UnifiedGuard needs to recognize an owner's connected store.
                                // Re-hydrate from /me (which returns user_role) so routing
                                // matches the password/OTP flow.
                                await checkAuth(true);
                                resolve({ success: true });
                            } else {
                                const errorMsg = response.data.error || 'Authentication failed';
                                resolve({ success: false, error: errorMsg.includes('account with this email already exists') ? 'Account already exists. Please sign in with your password.' : errorMsg });
                            }
                        } catch (error: any) {
                            console.error('Social auth error:', error);
                            const errorMsg = error.response?.data?.error || error.message || 'Authentication failed';
                            resolve({
                                success: false,
                                error: errorMsg.includes('account with this email already exists')
                                    ? 'Account already exists. Please sign in with your password.'
                                    : errorMsg,
                            });
                        } finally {
                            cleanup(messageHandler);
                        }
                        return;
                    }

                    if (type === 'SOCIAL_AUTH_ERROR' || typeof data.error === 'string' || typeof data.message === 'string') {
                        settled = true;
                        if (checkClosed) clearInterval(checkClosed);
                        window.removeEventListener('message', messageHandler as EventListener);
                        resolve({
                            success: false,
                            error: data.message || data.error || 'Authentication failed',
                        });
                        cleanup(messageHandler);
                    }
                };

                window.addEventListener('message', messageHandler as EventListener);

                popup = window.open(authUrl, 'facebook-auth', 'width=500,height=600,scrollbars=yes');
                if (!popup) {
                    cleanup(messageHandler);
                    resolve({ success: false, error: 'Popup blocked' });
                    return;
                }

                checkClosed = setInterval(() => {
                    const now = Date.now();
                    if (settled) return;

                    if (now - openedAt > maxMs) {
                        if (popup && !popup.closed) return;
                        settled = true;
                        cleanup(messageHandler);
                        console.warn('Facebook OAuth: authentication cancelled (timeout)');
                        resolve({ success: false, error: 'Authentication cancelled (timeout)' });
                        return;
                    }

                    if (!receivedMessage && now - openedAt > graceMs) {
                        const tokenAfter = localStorage.getItem('token');
                        if (tokenAfter && tokenAfter !== tokenBefore) {
                            settled = true;
                            cleanup(messageHandler);
                            console.warn('Facebook OAuth: token appeared; treating as success');
                            void checkAuth().finally(() => resolve({ success: true }));
                            return;
                        }

                        if (popup && !popup.closed) return;
                        settled = true;
                        cleanup(messageHandler);
                        console.warn('Facebook OAuth: authentication cancelled (no postMessage after grace)');
                        resolve({ success: false, error: 'Authentication cancelled' });
                    }
                }, 500);
            });
        } catch (error) {
            console.error('Facebook login error:', error);
            return { success: false, error: 'Facebook login failed' };
        }
    };

    const value: AuthContextType = {
        user,
        token,
        stores,
        currentShop,
        loading,
        currentPlan,
        currentSubscription,
        isFreePlan,
        billingDaysRemaining,
        hasFeature,
        hasPlanFeature,
        getPlanFeatureNumeric,
        getPlanFeatureText,
        canUsePredictions,
        getPredictionLimit,
        hasUnlimitedPredictions,
        login,
        register,
        loginWithGoogle,
        loginWithFacebook,
        sendLoginOtp,
        verifyLoginOtp,
        resendLoginOtp,
        logout,
        checkAuth,
        selectStore,
        refreshStores,
        loadSubscriptionData,
        updateUser,
        sessionExpiresAt,
    };

    return (
        <MultiStoreProvider>
            <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
        </MultiStoreProvider>
    );
}

export function EnhancedAuthProvider({ children }: AuthProviderProps) {
    return (
        <MultiStoreProvider>
            <AuthProvider>{children}</AuthProvider>
        </MultiStoreProvider>
    );
}
